import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { clearAdminDb, connectAdminDb } from "../lib/db";
import { getAuthToken, syncApiUrl, setAuthToken } from "@cliqsuite/db";

export interface AdminAuthUser {
  id: string;
  email: string;
  name: string;
  role: string;
}

interface AdminAuthValue {
  authed: boolean;
  user: AdminAuthUser | null;
  loading: boolean;
  error: string;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

const SESSION_KEY = "cliqsuite_admin_session_v1";

const AdminAuthContext = createContext<AdminAuthValue | null>(null);

function readSavedUser(): AdminAuthUser | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as { user?: AdminAuthUser };
    return saved.user ?? null;
  } catch {
    return null;
  }
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminAuthUser | null>(readSavedUser);
  const [loading, setLoading] = useState(() => user !== null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    const token = getAuthToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      try {
        localStorage.removeItem(SESSION_KEY);
      } catch {
        // Storage cleanup is best-effort.
      }
      return;
    }
    let cancelled = false;
    void fetch(syncApiUrl("/auth/session"), { headers: { authorization: `Bearer ${token}` } })
      .then(async (response) => {
        if (response.status === 401 || response.status === 403) return null;
        // A transient server/proxy failure is not evidence that the local
        // credential is invalid. Preserve the session so cached/offline UI
        // remains usable and the next reload can validate again.
        if (!response.ok) return undefined;
        return response.json() as Promise<{ scope?: string; sub?: string }>;
      })
      .then((session) => {
        if (cancelled) return;
        if (session === undefined) return;
        if (session && session.scope === "platform" && session.sub === user.id) return;
        setAuthToken(null);
        setUser(null);
        try {
          localStorage.removeItem(SESSION_KEY);
        } catch {
          // Storage cleanup is best-effort.
        }
      })
      .catch(() => {
        // Offline/DNS failure: keep the locally restored session. Scoped
        // collections have cached data where available and will reconnect
        // with the same token when connectivity returns.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // The saved identity is intentionally checked once before collections
    // mount. Login establishes a newly validated session directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(syncApiUrl("/auth/admin-login"), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        setError("Incorrect email or password.");
        return;
      }
      const body = (await res.json()) as { ok: boolean; user?: AdminAuthUser; token?: string };
      if (!body.ok || !body.user || !body.token) {
        setError("Incorrect email or password.");
        return;
      }
      setAuthToken(body.token);
      setUser(body.user);
      try {
        localStorage.setItem(SESSION_KEY, JSON.stringify({ user: body.user }));
      } catch {
        // reload-persistence is best-effort only
      }
    } catch {
      setError("Could not reach the server. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setAuthToken(null);
    setUser(null);
    try {
      localStorage.removeItem(SESSION_KEY);
    } catch {
      // ignore
    }
    // Wipe the local database, then hard-navigate so the module-level collections drop their rows too.
    void clearAdminDb().finally(() => {
      window.location.href = "/";
    });
  }, []);

  useEffect(() => {
    if (user) void connectAdminDb(user.id);
  }, [user?.id]);

  const clearError = useCallback(() => setError(""), []);

  const value = useMemo<AdminAuthValue>(
    () => ({ authed: !!user, user, loading, error, login, logout, clearError }),
    [user, loading, error, login, logout, clearError],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth(): AdminAuthValue {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
